import { Button, FormControl, IconButton, InputAdornment, MenuItem, Stack } from "@mui/material";
import { FormRadioGroup } from "component/FormRadioGroup";
import { FormSelect } from "component/FormSelect";
import { ChevronDownIcon, ClearIcon, DoubleArrowUpIcon } from "component/Icons";
import { CustomSelect } from "../../component/CustomSelect";
import { MEDICAL_TEAM_OPTIONS, PATIENT_HANDOVER_TYPE_OPTIONS, MULTIPATIENT_PRIORITY_SELECT_ITEMS } from "constant";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { COLORS } from "style";
import { Hospital } from "type/model/api";
import { CreateSecondaryMissionFormProps, CreateSecondaryMissionTaskSchemaProps } from "type/schema";
import { CustomInputLabel } from "component/CustomInputLabel";
import { FormTextInput } from "component/FormTextInput";
import { ErrorText } from "component/ErrorMessage";

interface Props {
  hospitals: Hospital[];
  defaultTaskValues: CreateSecondaryMissionTaskSchemaProps;
}

export const MultiPatientDetails = ({ hospitals, defaultTaskValues }: Props) => {
  const { control, formState } = useFormContext<Partial<CreateSecondaryMissionFormProps>>();
  const { fields, append, remove } = useFieldArray({
    name: "tasks",
  });
  return (
    <>
      {fields.map((field, index) => (
        <Stack
          key={field.id}
          sx={{
            gap: 4,
            alignItems: "flex-start",
            border: `2px solid ${COLORS.WHITE_SMOKE}`,
            borderRadius: "5px",
            padding: 4,
            position: "relative",
          }}
        >
          {fields.length > 1 ? (
            <Stack sx={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}>
              <IconButton onClick={() => remove(index)}>
                <ClearIcon />
              </IconButton>
            </Stack>
          ) : null}

          <Controller
            name={`tasks.${index}.patientIdentifier`}
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormTextInput
                label="Patient No."
                placeholder="Enter patient identifier"
                fullWidth
                required
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                sx={{ maxWidth: 350 }}
                type={"text"}
              />
            )}
          />
          <Controller
            name={`tasks.${index}.priority`}
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormSelect
                label="Priority"
                required
                placeholder="Select a priority"
                displayEmpty={true}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                sx={{ minWidth: 350 }}
                startAdornment={
                  <InputAdornment position="start">
                    <DoubleArrowUpIcon />
                  </InputAdornment>
                }
                items={MULTIPATIENT_PRIORITY_SELECT_ITEMS}
              />
            )}
          />

          <Stack
            direction="row"
            sx={{ width: "100%", justifyContent: "space-between", alignItems: "flex-end", gap: 1 }}
          >
            <Controller
              name={`tasks.${index}.referringHospital`}
              control={control}
              render={({ field }) => (
                <FormControl>
                  <CustomInputLabel sx={{ marginBottom: "8px" }} required>
                    Referring Hospital
                  </CustomInputLabel>
                  <CustomSelect
                    placeholder="Select a referring hospital"
                    displayEmpty={true}
                    renderValue={(value) => {
                      const hospital = hospitals.find((hospital) => hospital.id === value);
                      return hospital ? hospital.name : "Select a hospital";
                    }}
                    {...field}
                    size="small"
                    sx={{ minWidth: 350 }}
                    variant="outlined"
                    IconComponent={ChevronDownIcon}
                    MenuProps={{
                      disableAutoFocusItem: true,
                      MenuListProps: { sx: { bgcolor: COLORS.DR_WHITE } },
                    }}
                  >
                    {hospitals.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </FormControl>
              )}
            />
            <Controller
              name={`tasks.${index}.referringHandoverType`}
              control={control}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <FormSelect
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={error?.message}
                  sx={{ minWidth: 350 }}
                  items={PATIENT_HANDOVER_TYPE_OPTIONS}
                />
              )}
            />
          </Stack>

          {formState.errors?.tasks?.[index]?.referringHospital ? (
            <Stack sx={{ mt: -3 }}>
              <ErrorText>{formState.errors!.tasks[index]!.referringHospital!.message}</ErrorText>
            </Stack>
          ) : null}

          <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between", alignItems: "flex-end" }}>
            <Controller
              name={`tasks.${index}.destinationHospital`}
              control={control}
              render={({ field }) => (
                <FormControl>
                  <CustomInputLabel sx={{ marginBottom: "8px" }} required>
                    Destination Hospital
                  </CustomInputLabel>
                  <CustomSelect
                    placeholder="Select a destination hospital"
                    displayEmpty={true}
                    renderValue={(value) => {
                      const hospital = hospitals.find((hospital) => hospital.id === value);
                      return hospital ? hospital.name : "Select a hospital";
                    }}
                    {...field}
                    size="small"
                    sx={{ minWidth: 350 }}
                    variant="outlined"
                    IconComponent={ChevronDownIcon}
                    MenuProps={{
                      disableAutoFocusItem: true,
                      MenuListProps: { sx: { bgcolor: COLORS.DR_WHITE } },
                    }}
                  >
                    {hospitals.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </FormControl>
              )}
            />
            <Controller
              name={`tasks.${index}.destinationHandoverType`}
              control={control}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <FormSelect
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={error?.message}
                  sx={{ minWidth: 350 }}
                  items={PATIENT_HANDOVER_TYPE_OPTIONS}
                />
              )}
            />
          </Stack>

          {formState.errors?.tasks?.[index]?.destinationHospital ? (
            <Stack sx={{ mt: -3 }}>
              <ErrorText>{formState.errors!.tasks[index]!.destinationHospital!.message}</ErrorText>
            </Stack>
          ) : null}

          <Controller
            name={`tasks.${index}.medicalTeam`}
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormRadioGroup
                fullWidth
                row
                label="Medical team"
                required
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                items={MEDICAL_TEAM_OPTIONS}
              />
            )}
          />
          <Controller
            name={`tasks.${index}.needsStretcher`}
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormRadioGroup
                fullWidth
                row
                label="Patient mobility"
                required
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                items={[
                  { label: "Ambulant", value: "false" },
                  { label: "Stretcher", value: "true" },
                ]}
              />
            )}
          />

          <Controller
            name={`tasks.${index}.canTravelWithOthers`}
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormRadioGroup
                fullWidth
                row
                label="May travel with other patients"
                required
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                items={[
                  { label: "Yes", value: "true" },
                  { label: "No", value: "false" },
                ]}
              />
            )}
          />
        </Stack>
      ))}
      {fields.length < 12 ? (
        <Button variant="text" sx={{ color: COLORS.HAILEY_BLUE, p: 0 }} onClick={() => append(defaultTaskValues)}>
          + Add another patient
        </Button>
      ) : null}
    </>
  );
};
